import { ACCOUNT_TYPES } from "@/lib/constants";
import {
  Account,
  Attachment,
  Budget,
  BudgetCategory,
  BudgetSubCategory,
  BudgetSummary,
  Currency,
  DocumentType,
  ExchangeRate,
  Expense,
  Funding,
  Income,
  Transaction,
  Transfer,
  Vendor,
  Vessel,
  Invoice,
  InvoiceLineItem,
  InvoiceAttachment,
  InvoiceSummary,
  InvoiceStatistics,
  InvoicePayment,
  MaintenanceTask,
  MaintenanceExecutionEntry,
  SubscriptionSummary,
} from "@/lib/types";
import { formatDateToISOString } from "@/lib/utils";
import {
  LoginResponse,
  MfaChallengeResponse,
  RefreshTokenResponse,
} from "@/services/mutations/auth";
import { LoginOrganization } from "@/lib/types";
import { addDays, subDays, subMonths } from "date-fns";

const commonOrgId = "org-1";

export const mockSubscriptionSummaryActiveTrial: SubscriptionSummary = {
  plan: "Trial",
  status: "active",
  trialEndsAt: addDays(new Date(), 7).toISOString(),
  daysRemaining: 7,
  vesselLimit: 1,
  vesselCount: 1,
  canAddVessel: false,
};

export const mockSubscriptionSummaryExpired: SubscriptionSummary = {
  plan: "Trial",
  status: "expired",
  trialEndsAt: subDays(new Date(), 3).toISOString(),
  daysRemaining: 0,
  vesselLimit: 1,
  vesselCount: 1,
  canAddVessel: false,
};

export const mockSubscriptionSummaryUnlimited: SubscriptionSummary = {
  plan: "Pro",
  status: "active",
  trialEndsAt: null,
  daysRemaining: null,
  vesselLimit: 0,
  vesselCount: 3,
  canAddVessel: true,
};

export const mockLoginResponse: LoginResponse = {
  accessToken:
    "eyJhbGciOiJSUzI1NiIsInR5cCIgOiAiSldUIiwia2lkIiA6ICJRU296ZXZZUXhBZXZ0QnczNWdRem9yckdDODRDOG42bG5rMEllYTNGNEdFIn0.eyJleHAiOjE3Njk1MzUwNDMsImlhdCI6MTc2OTUyNDI0MywianRpIjoiYTMzZjZiZGUtMmY5NS00NzZjLTkxNTQtZWJiYTY0NjY0YTk0IiwiaXNzIjoiaHR0cDovL2tleWNsb2FrOjgwODAvcmVhbG1zL3lhdGJvb2tzX2RldiIsImF1ZCI6InlhdGJvb2tzLWJhY2tlbmQiLCJzdWIiOiIzMDYyMzg1OS03OGM3LTRiZjEtYjMzZi1hNGQ2ZTUzOTFlNjEiLCJ0eXAiOiJCZWFyZXIiLCJhenAiOiJ5YXRib29rcy1iYWNrZW5kIiwic2lkIjoiMWE2OGE4ZjEtY2MzMi00NGY4LTliZTAtMmQ5MTIzN2E5ZmFkIiwic2NvcGUiOiJwcm9maWxlIHlhdGJvb2tzLXVzZXItc2NvcGUgZW1haWwiLCJlbWFpbF92ZXJpZmllZCI6dHJ1ZSwicm9sZSI6ImFkbWluIiwib3JnX2lkIjoiMDE5NjhmYmEtZTlhNi03N2U5LWIwYzAtYzJlNTI4YTIwZDdjIiwicGVybWlzc2lvbnMiOlsiY3VycmVuY2llcy5yZWFkIiwiZXhwZW5zZXMud3JpdGUiLCJjcmV3LnJlYWQiLCJjdXJyZW5jaWVzLndyaXRlIiwic2V0dGluZ3MucmVhZCIsImFjY291bnRzLndyaXRlIiwiYWNjb3VudHMucmVhZCIsIm1haW50ZW5hbmNlLWFzc2lnbm1lbnQud3JpdGUiLCJtYWludGVuYW5jZS1tYW5hZ2VtZW50LndyaXRlIiwibWFpbnRlbmFuY2UtcnVubmluZy1ob3Vycy53cml0ZSIsIm1haW50ZW5hbmNlLnJlYWQiLCJtYWludGVuYW5jZS1leGVjdXRpb24ud3JpdGUiLCJ2ZXNzZWxzLnJlYWQiLCJidWRnZXRzLndyaXRlIiwidXNlcnMud3JpdGUiLCJjcmV3LndyaXRlIiwidmVzc2Vscy53cml0ZSIsInN1YnNjcmlwdGlvbnMucmVhZCIsImJ1ZGdldHMucmVhZCIsInN1YnNjcmlwdGlvbnMud3JpdGUiLCJzZXR0aW5ncy53cml0ZSIsImV4cGVuc2VzLnJlYWQiLCJ1c2Vycy5yZWFkIl0sIm5hbWUiOiJNZXZsw7x0IENhbiBUdW5hIiwidmVzc2VsX2lkcyI6WyIwMTk2YjlhOS1mNzZiLTdlMzUtYWUxZS1mMDYzY2QyNDQ4NzEiXSwicHJlZmVycmVkX3VzZXJuYW1lIjoibXR0dW5hOTBAZ21haWwuY29tIiwiZ2l2ZW5fbmFtZSI6Ik1ldmzDvHQgQ2FuIiwiZmFtaWx5X25hbWUiOiJUdW5hIiwiZW1haWwiOiJtdHR1bmE5MEBnbWFpbC5jb20ifQ.EFdAJBvDDl5d_vPFNXmi4BDBe2d7e2-J6wTBG8DSaViwgK8Rnb2SlS8lY3PUNneJ1VuZk4OLaMktxMbPnZ7vXXbfdhFQEqEFDFNN1dUg_aldyCoq7UlntNoRMp3QVnCkwbAvc_mHCouKjNLvTLmDb1Kx6JL-PEomwFdZugazEMt4ZuHR5tSjY5waO0m5wRYAN-2IAUyZGcltGi7NZ65jlnWamOP_-1oMVjUydukhnCAwtj2zo0pkmD-eQbzLeB66OB1BI_52YOnebDqwSHvYN9kLvkwzqzb-zz7AZM-Jpvjv9A3aa6_MGQpxQNhf_T68gJvn8_C-Hj2-hJCSIQ0ObQ",
  expiresIn: 300,
  notBeforePolicy: 0,
  refreshExpiresIn: 1800,
  refreshToken:
    "eyJhbGciOiJIUzUxMiIsInR5cCIgOiAiSldUIiwia2lkIiA6ICJjYjUxZDdlMi1hNDQ2LTQ4YTctYjhlZC00MWU0MGQwNGQzYjcifQ.eyJleHAiOjE3MzcyNzI3OTMsImlhdCI6MTczNzI3MDk5MywianRpIjoiMGE0ZjM3YWItYzdjMS00YmUxLWJlZDctMjdkNzIyNjBmM2E1IiwiaXNzIjoiaHR0cDovL2tleWNsb2FrOjgwODAvcmVhbG1zL3lhdGJvb2tzX2RldiIsImF1ZCI6Imh0dHA6Ly9rZXljbG9hazo4MDgwL3JlYWxtcy95YXRib29rc19kZXYiLCJ0eXAiOiJSZWZyZXNoIiwiYXpwIjoieWF0Ym9va3MtYmFja2VuZCIsInNpZCI6IjEyOTE4OTYyLWI5MzktNDQ0NC04NTI1LTAwM2UwZWNiNTk1NyIsInNjb3BlIjoicHJvZmlsZSB5YXRib29rcy11c2VyLXNjb3BlIn0.MaLMTjRj_ZhMIsaVN1Fn2JLHDyOV3LfD5w98fifpfKFYl-rTczACiXFHIoLnhCrcbfo7ojLkH5KLZjpaSG3OLA",
  scope: "profile yatbooks-user-scope",
  sessionState: "12918962-b939-4444-8525-003e0ecb5957",
  tokenType: "Bearer",
};

export const mockRefreshTokenResponse: RefreshTokenResponse = {
  accessToken: "mockAccessToken",
  expiresIn: 300,
  notBeforePolicy: 0,
  refreshExpiresIn: 1800,
  refreshToken: "mockRefreshToken",
  scope: "profile yatbooks-user-scope",
  sessionState: "12918962-b939-4444-8525-003e0ecb5957",
  tokenType: "Bearer",
};

export const mockMfaChallengeResponse: MfaChallengeResponse = {
  mfaRequired: true,
  challengeId: "5b1d6f0e-8a4c-4f5e-9d2b-7c3a1e9f0b42",
  method: "totp",
};

export const mockLoginOrganizations: LoginOrganization[] = [
  {
    organizationId: "01968fba-e9a6-77e9-b0c0-c2e528a20d7c",
    organizationName: "Primary Org",
    roleId: "role-1",
    roleName: "Admin",
  },
];

export const mockCurrencies: Currency[] = [
  {
    id: "0195a53d-a3c5-719d-9f1b-55cd186917bb",
    code: "EUR",
    name: "Euro",
    symbol: "€",
    decimalPlaces: 2,
    isActive: true,
    version: 1,
  },
  {
    id: "0195a53d-d5af-7559-b02f-2c4b6a4580fe",
    code: "USD",
    name: "US Dollar",
    symbol: "$",
    decimalPlaces: 2,
    isActive: true,
    version: 1,
  },
  {
    id: "0195a53e-3931-7b87-8909-3af7115c185b",
    code: "TRY",
    name: "Turkish Lira",
    symbol: "₺",
    decimalPlaces: 2,
    isActive: true,
    version: 1,
  },
  {
    id: "0195a53e-3931-7b87-8909-3af7115c185c",
    code: "GBP",
    name: "British Pound",
    symbol: "£",
    decimalPlaces: 2,
    isActive: true,
    version: 1,
  },
];

export const mockVessels: Vessel[] = [
  {
    id: "019494c1-5d92-7538-ade4-915375996156",
    name: "TechnoMarine",
    registrationNumber: "TM-2020-456",
    make: "Sunseeker",
    model: "Manhattan 68",
    yearBuilt: 2020,
    lengthInFeet: 68,
    hullIdentificationNumber: "SUN68M2020TM456",
    homePort: "Monaco",
    flagState: "Malta",
    notes: "Luxury yacht with modern amenities",
    isActive: true,
    version: 1,
    imageUrl: "https://example.com/images/technomarine.jpg",
    organizationId: commonOrgId,
    ownerId: "0195a53d-a3c5-719d-9f1b-55cd186917cc",
    defaultCurrencyId: mockCurrencies[0].id,
    defaultCurrencyCode: mockCurrencies[0].code,
    crew: [
      {
        id: "crew-1",
        userId: "user-1",
        userName: "john.smith",
        fullName: "John Smith",
        profilePictureUrl: "https://example.com/profiles/john.jpg",
        role: "Captain",
        assignedDate: "2023-01-15",
        isActive: true,
      },
      {
        id: "crew-2",
        userId: "user-2",
        userName: "maria.garcia",
        fullName: "Maria Garcia",
        profilePictureUrl: "https://example.com/profiles/maria.jpg",
        role: "Engineer",
        assignedDate: "2023-01-15",
        isActive: true,
      },
    ],
  },
  {
    id: "01949ce9-9d7b-7347-9320-7cba01165713",
    name: "Ereiva",
    registrationNumber: "ER-2019-789",
    make: "Azimut",
    model: "Flybridge 55",
    yearBuilt: 2019,
    lengthInFeet: 55,
    hullIdentificationNumber: "AZI55F2019ER789",
    homePort: "Istanbul",
    flagState: "Turkey",
    notes: "Mid-size yacht for coastal cruising",
    imageUrl: "https://example.com/images/ereiva.jpg",
    isActive: false,
    organizationId: commonOrgId,
    ownerId: "0195a53d-a3c5-719d-9f1b-55cd186917dd",
    version: 2,
    defaultCurrencyId: mockCurrencies[2].id,
    defaultCurrencyCode: mockCurrencies[2].code,
    crew: [
      {
        id: "crew-3",
        userId: "user-3",
        userName: "ahmet.yilmaz",
        fullName: "Ahmet Yilmaz",
        profilePictureUrl: "https://example.com/profiles/ahmet.jpg",
        role: "Captain",
        assignedDate: "2023-01-15",
        isActive: true,
      },
    ],
  },
];

export const mockAccounts: Account[] = [
  {
    id: "0195cc4e-4a87-7f8b-b526-98d7767aeba4",
    organizationId: commonOrgId,
    name: "BNP EUR",
    accountNumber: "6656838",
    type: ACCOUNT_TYPES[0].value,
    currentBalance: 100000,
    isActive: true,
    notes: "",
    version: 1,
    currencyId: mockCurrencies[0].id,
    vesselId: mockVessels[0].id,
    parentAccountId: "",
    parentAccountName: "",
    userAssignments: [
      {
        id: "assignment-1",
        userId: "user-1",
        userName: "john.doe",
        fullName: "John Doe",
        profilePictureUrl: "https://example.com/profiles/john.jpg",
        assignedDate: "2023-01-15",
        isActive: true,
      },
    ],
  },
  {
    id: "0195cc4e-4a87-7f8b-b526-98d7767aeba5",
    organizationId: commonOrgId,
    name: "JPM USD",
    accountNumber: "6656839",
    type: ACCOUNT_TYPES[1].value,
    currentBalance: 150000,
    isActive: true,
    notes: "",
    currencyId: mockCurrencies[1].id,
    version: 1,
    vesselId: mockVessels[0].id,
    parentAccountId: "",
    parentAccountName: "",
    userAssignments: [
      {
        id: "assignment-2",
        userId: "user-2",
        userName: "maria.garcia",
        fullName: "Maria Garcia",
        profilePictureUrl: "https://example.com/profiles/maria.jpg",
        assignedDate: "2023-01-15",
        isActive: true,
      },
    ],
  },
  {
    id: "0195cc4e-4a87-7f8b-b526-98d7767aeba6",
    organizationId: commonOrgId,
    name: "Garanti TRY",
    accountNumber: "7756838",
    type: ACCOUNT_TYPES[0].value,
    currentBalance: 500000,
    isActive: true,
    notes: "",
    currencyId: mockCurrencies[2].id,
    version: 1,
    vesselId: mockVessels[1].id,
    parentAccountId: "",
    parentAccountName: "",
    userAssignments: [
      {
        id: "assignment-3",
        userId: "user-3",
        userName: "ahmet.yilmaz",
        fullName: "Ahmet Yilmaz",
        profilePictureUrl: "https://example.com/profiles/ahmet.jpg",
        assignedDate: "2023-01-15",
        isActive: true,
      },
    ],
  },
  {
    id: "0195cc4e-4a87-7f8b-b526-98d7767aeba7",
    organizationId: commonOrgId,
    name: "HSBC USD",
    accountNumber: "7756839",
    type: ACCOUNT_TYPES[1].value,
    currentBalance: 75000,
    isActive: true,
    notes: "",
    currencyId: mockCurrencies[1].id,
    version: 1,
    vesselId: mockVessels[1].id,
    parentAccountId: "",
    parentAccountName: "",
    userAssignments: [
      {
        id: "assignment-4",
        userId: "user-4",
        userName: "sarah.smith",
        fullName: "Sarah Smith",
        profilePictureUrl: "https://example.com/profiles/sarah.jpg",
        assignedDate: "2023-01-15",
        isActive: true,
      },
    ],
  },
];

export const mockBudgets: Budget[] = [
  {
    baseCurrencyId: mockVessels[0].defaultCurrencyId,
    endDate: "2026-01-01T00:00:00Z",
    id: "0194ae0b-a5c7-7536-a33b-ef9f6903ac0e",
    name: "2025",
    parentBudgetId: null,
    startDate: "2025-01-01T00:00:00Z",
    status: "Draft",
    templateId: "0194ae00-bf22-72a3-aed7-c4f2257fd9e4",
    totalAmount: 0,
    vesselId: mockVessels[0].id,
  },
  {
    baseCurrencyId: mockVessels[0].defaultCurrencyId,
    endDate: "2026-01-01T00:00:00Z",
    id: "0194ae0b-a5c7-7536-a33b-ef9f6903ac0f",
    name: "2026",
    parentBudgetId: null,
    startDate: "2026-01-01T00:00:00Z",
    status: "Draft",
    templateId: "0194ae00-bf22-72a3-aed7-c4f2257fd9e1",
    totalAmount: 0,
    vesselId: mockVessels[0].id,
  },
  {
    baseCurrencyId: mockVessels[1].defaultCurrencyId,
    endDate: "2026-01-01T00:00:00Z",
    id: "0194ae0b-a5c7-7536-a33b-323232323232",
    name: "2027",
    parentBudgetId: null,
    startDate: "2027-01-01T00:00:00Z",
    status: "Draft",
    templateId: "0194ae00-bf22-72a3-aed7-c4f2257fd9e4",
    totalAmount: 0,
    vesselId: mockVessels[1].id,
  },
];

export const mockBudgetCategories: BudgetCategory[] = [
  {
    budgetId: mockBudgets[0].id,
    description: "",
    id: "0195ad70-a616-7cae-86c3-c4ad0d2b484d",
    isCustom: false,
    name: "Administration",
    order: 1,
    totalAmount: 100000,
    allocatedAmount: 100000,
    remainingAmount: 0,
    currencyId: mockBudgets[0].baseCurrencyId,
    currencyCode: mockCurrencies[0].code,
  },
  {
    id: "0195ad71-4681-7d9b-9f5f-3f1030d0e9c51",
    name: "Operation",
    description: "",
    order: 2,
    isCustom: false,
    budgetId: mockBudgets[0].id,
    totalAmount: 100000,
    allocatedAmount: 100000,
    remainingAmount: 0,
    currencyId: mockBudgets[0].baseCurrencyId,
    currencyCode: mockCurrencies[0].code,
  },
  {
    id: "0195ad71-7ebb-7e94-a5a9-189b29d91347",
    name: "Maintenance",
    description: "",
    order: 3,
    isCustom: false,
    budgetId: mockBudgets[1].id,
    totalAmount: 100000,
    allocatedAmount: 100000,
    remainingAmount: 0,
    currencyId: mockBudgets[1].baseCurrencyId,
    currencyCode: mockCurrencies[2].code,
  },
];

export const mockBudgetSubCategories: BudgetSubCategory[] = [
  {
    id: "0195ad71-4681-7d9b-9f5f-3f1030d0e9c51",
    name: "Haul Out Year Survey",
    description: "",
    order: 1,
    isCustom: false,
    categoryId: mockBudgetCategories[0].id,
    totalAmount: 100000,
    allocatedAmount: 100000,
    remainingAmount: 0,
    currencyId: mockBudgetCategories[0].currencyId,
    currencyCode: mockBudgetCategories[0].currencyCode,
  },
  {
    id: "0195ad71-4681-7d9b-9f5f-3f1030d0e9c52",
    name: "Deck Works ( Teaks, Tables, Anchor Pockets etc)",
    description: "",
    order: 2,
    isCustom: false,
    categoryId: mockBudgetCategories[1].id,
    totalAmount: 100000,
    allocatedAmount: 100000,
    remainingAmount: 0,
    currencyId: mockBudgetCategories[1].currencyId,
    currencyCode: mockBudgetCategories[1].currencyCode,
  },
  {
    id: "0195ad71-4681-7d9b-9f5f-3f1030d0e9c53",
    name: "Paint Works",
    description: "",
    order: 3,
    isCustom: false,
    categoryId: mockBudgetCategories[1].id,
    totalAmount: 100000,
    allocatedAmount: 100000,
    remainingAmount: 0,
    currencyId: mockBudgetCategories[1].currencyId,
    currencyCode: mockBudgetCategories[1].currencyCode,
  },
  {
    id: "0195ad7b-041d-722c-a5da-0a90abcc5d164",
    name: "Engine Department Additional Works",
    description: "",
    order: 4,
    isCustom: false,
    categoryId: mockBudgetCategories[2].id,
    totalAmount: 100000,
    allocatedAmount: 100000,
    remainingAmount: 0,
    currencyId: mockBudgetCategories[2].currencyId,
    currencyCode: mockBudgetCategories[2].currencyCode,
  },
  {
    id: "0195ad7b-2c47-7912-b1db-22576fbc81e65",
    name: "Stabilizer Year Maintenance",
    description: "",
    order: 5,
    isCustom: false,
    categoryId: mockBudgetCategories[2].id,
    totalAmount: 100000,
    allocatedAmount: 100000,
    remainingAmount: 0,
    currencyId: mockBudgetCategories[2].currencyId,
    currencyCode: mockBudgetCategories[2].currencyCode,
  },
  {
    id: "0195ad7b-4865-752d-b75f-2d6912700738",
    name: "MTU annual",
    description: "",
    order: 6,
    isCustom: false,
    categoryId: mockBudgetCategories[2].id,
    totalAmount: 100000,
    allocatedAmount: 100000,
    remainingAmount: 0,
    currencyId: mockBudgetCategories[2].currencyId,
    currencyCode: mockBudgetCategories[2].currencyCode,
  },
];

export const mockVendors: Vendor[] = [
  {
    id: "vendor-001",
    organizationId: commonOrgId,
    name: "Marine Engine Supplies Ltd.",
    contactPerson: "John Smith",
    email: "john.smith@marineengine.com",
    phone: "+44 123 456 7890",
    website: "www.marineenginesupplies.com",
    notes: "Preferred supplier for engine parts",
    iban: "GB29NWBK60161331926819",
    swiftCode: "NWBKGB2L",
    bankName: "National Westminster Bank",
    bankAddress: "1 Princess Street, London, EC2R 8PB",
    bankAccountNumber: "31926819",
    accountHolderName: "Marine Engine Supplies Ltd.",
    paymentReference: "MES001",
    version: 1,
  },
  {
    id: "vendor-002",
    organizationId: commonOrgId,
    name: "Acme Supplies Ltd.",
    contactPerson: "Emma Johnson",
    email: "emma@yachtinteriors.com",
    phone: "+33 987 654 3210",
    website: "www.yachtinteriordesigns.com",
    notes: "Specializes in luxury yacht furnishings",
    iban: "FR1420041010050500013M02606",
    swiftCode: "BNPAFRPP",
    bankName: "BNP Paribas",
    bankAddress: "16 Boulevard des Italiens, 75009 Paris, France",
    bankAccountNumber: "50500013M026",
    accountHolderName: "Yacht Interior Designs SAS",
    paymentReference: "YID002",
    version: 1,
  },
  {
    id: "vendor-003",
    organizationId: commonOrgId,
    name: "Global Marine Services",
    contactPerson: "Michael Chen",
    email: "m.chen@globalmarine.com",
    phone: "+1 305 555 7777",
    website: "www.globalmarine.com",
    notes: "Worldwide maintenance and repair services",
    iban: "US45CHAS3150045678901",
    swiftCode: "CHASUS33",
    bankName: "JPMorgan Chase Bank",
    bankAddress: "270 Park Avenue, New York, NY 10017, USA",
    bankAccountNumber: "3150045678901",
    accountHolderName: "Global Marine Services Inc.",
    paymentReference: "GMS003",
    version: 1,
  },
];

export const mockExchangeRates: ExchangeRate[] = [
  {
    id: "er-eur-usd-uuid",
    fromCurrencyId: mockCurrencies[0].id,
    toCurrencyId: mockCurrencies[1].id,
    rate: 1.07,
    fetchedAt: "2024-01-01T00:00:00Z",
    version: 1,
  },
  {
    id: "er-usd-eur-uuid",
    fromCurrencyId: mockCurrencies[1].id,
    toCurrencyId: mockCurrencies[0].id,
    rate: 0.93,
    fetchedAt: "2024-01-01T00:00:00Z",
    version: 1,
  },
  {
    id: "er-eur-try-uuid",
    fromCurrencyId: mockCurrencies[0].id,
    toCurrencyId: mockCurrencies[2].id,
    rate: 35.12,
    fetchedAt: "2024-01-01T00:00:00Z",
    version: 1,
  },
  {
    id: "er-try-eur-uuid",
    fromCurrencyId: mockCurrencies[2].id,
    toCurrencyId: mockCurrencies[0].id,
    rate: 0.028,
    fetchedAt: "2024-01-01T00:00:00Z",
    version: 1,
  },
  {
    id: "er-usd-try-uuid",
    fromCurrencyId: mockCurrencies[1].id,
    toCurrencyId: mockCurrencies[2].id,
    rate: 32.82,
    fetchedAt: "2024-01-01T00:00:00Z",
    version: 1,
  },
  {
    id: "er-try-usd-uuid",
    fromCurrencyId: mockCurrencies[2].id,
    toCurrencyId: mockCurrencies[1].id,
    rate: 0.03,
    fetchedAt: "2024-01-01T00:00:00Z",
    version: 1,
  },
];

export const mockAttachments: Attachment[] = [
  {
    contentType: "application/pdf",
    description: "",
    fileName: "1744343723026.pdf",
    filePath:
      "transactions/01979379-ee56-71f4-8aec-3bedc1a412d5/f6daa1f9-8c33-4747-88fb-bb0e0adc6ac0-1744343723026.pdf",
    fileSize: 60028,
    id: "att-1",
  },
  {
    contentType: "image/jpeg",
    description: "",
    fileName: "1744343723027.jpg",
    filePath:
      "transactions/01979379-ee56-71f4-8aec-3bedc1a412d5/f6daa1f9-8c33-4747-88fb-bb0e0adc6ac0-1744343723027.jpg",
    fileSize: 60028,
    id: "att-2",
  },
];

export const mockAttachmentUrls: { id: string; url: string }[] = [
  {
    id: "att-1",
    url: "http://example.com/existing-file.pdf",
  },
  {
    id: "att-2",
    url: "http://example.com/existing-file.jpg",
  },
];

export const mockExpenses: Expense[] = [
  {
    id: "expense-001",
    transactionDate: formatDateToISOString(
      new Date(new Date().setHours(6, 0, 0, 0))
    ),
    status: "Completed",
    amount: 4444444.44,
    description: "Haul Out Year Survey",
    reference: "REF-001",
    organizationId: commonOrgId,
    currencyId: mockAccounts[0].currencyId,
    budgetId: mockBudgets[0].id,
    vendorId: mockVendors[0].id,
    fromAccountId: mockAccounts[0].id,
    vesselId: mockVessels[0].id,
    attachments: [],
    type: "Expense",
    currencyCode: mockCurrencies[0].code,
    fromAccountName: mockAccounts[0].name,
    vendorName: mockVendors[0].name,
    version: 1,
    vesselFlagState: mockVessels[0].flagState,
    vesselName: mockVessels[0].name,
    exchangeRateId: mockExchangeRates[0].id,
    conversionRate: 1,
    ledgerEntries: [],
  },
  {
    id: "expense-002",
    transactionDate: formatDateToISOString(
      addDays(new Date(new Date().setHours(6, 0, 0, 0)), 1)
    ),
    status: "Draft",
    amount: 2500000.5,
    description: "Engine Maintenance",
    reference: "REF-002",
    organizationId: commonOrgId,
    currencyId: mockAccounts[2].currencyId,
    budgetId: mockBudgets[1].id,
    vendorId: mockVendors[1].id,
    fromAccountId: mockAccounts[2].id,
    vesselId: mockVessels[1].id,
    attachments: [],
    type: "Expense",
    currencyCode: mockCurrencies[2].code,
    fromAccountName: mockAccounts[2].name,
    vendorName: mockVendors[1].name,
    version: 1,
    vesselFlagState: mockVessels[1].flagState,
    vesselName: mockVessels[1].name,
    ledgerEntries: [],
  },
  {
    id: "expense-003",
    transactionDate: formatDateToISOString(new Date()),
    status: "Pending",
    amount: 1500000.5,
    description: "Interior Renovation",
    reference: "REF-003",
    organizationId: commonOrgId,
    currencyId: mockAccounts[0].currencyId,
    budgetId: mockBudgets[0].id,
    vendorId: mockVendors[2].id,
    fromAccountId: mockAccounts[0].id,
    vesselId: mockVessels[0].id,
    attachments: [],
    type: "Expense",
    currencyCode: mockCurrencies[0].code,
    fromAccountName: mockAccounts[0].name,
    vendorName: mockVendors[2].name,
    version: 1,
    vesselFlagState: mockVessels[0].flagState,
    vesselName: mockVessels[0].name,
    ledgerEntries: [],
  },
  {
    id: "expense-004",
    transactionDate: formatDateToISOString(new Date()),
    status: "Completed",
    amount: 3100000.5,
    description: "Navigation System Upgrade",
    reference: "REF-004",
    organizationId: commonOrgId,
    currencyId: mockAccounts[3].currencyId,
    budgetId: mockBudgets[1].id,
    vendorId: mockVendors[0].id,
    fromAccountId: mockAccounts[3].id,
    vesselId: mockVessels[1].id,
    attachments: [],
    type: "Expense",
    currencyCode: mockCurrencies[3].code,
    fromAccountName: mockAccounts[3].name,
    vendorName: mockVendors[0].name,
    version: 1,
    vesselFlagState: mockVessels[1].flagState,
    vesselName: mockVessels[1].name,
    ledgerEntries: [],
  },
  {
    id: "expense-005",
    transactionDate: formatDateToISOString(
      new Date(
        new Date().setFullYear(
          new Date().getFullYear(),
          new Date().getMonth() + 1,
          15
        )
      )
    ),
    status: "Completed",
    amount: 1000000.5,
    description: "Engine Maintenance",
    reference: "REF-001",
    organizationId: commonOrgId,
    currencyId: mockAccounts[2].currencyId,
    budgetId: mockBudgets[1].id,
    vendorId: mockVendors[1].id,
    fromAccountId: mockAccounts[2].id,
    vesselId: mockVessels[1].id,
    attachments: [],
    type: "Expense",
    currencyCode: mockCurrencies[2].code,
    fromAccountName: mockAccounts[2].name,
    vendorName: mockVendors[1].name,
    version: 1,
    vesselFlagState: mockVessels[1].flagState,
    vesselName: mockVessels[1].name,
    ledgerEntries: [],
  },
];

export const mockIncomes: Income[] = [
  {
    id: "income-001",
    transactionDate: formatDateToISOString(new Date()),
    invoiceDate: formatDateToISOString(new Date()),
    dueDate: formatDateToISOString(new Date()),
    status: "Completed",
    amount: 1000000.5,
    description: "Engine Maintenance",
    reference: "REF-001",
    organizationId: commonOrgId,
    currencyId: mockAccounts[0].currencyId,
    currencyCode: mockCurrencies[0].code,
    budgetId: mockBudgets[0].id,
    budgetName: "Maintenance Budget",
    source: "Client Payment",
    accountId: mockAccounts[0].id,
    accountName: mockAccounts[0].name,
    vesselId: mockVessels[0].id,
    vesselName: mockVessels[0].name,
    exchangeRateId: mockExchangeRates[0].id,
    conversionRate: 1,
    type: "Income",
  },
  {
    id: "income-002",
    transactionDate: formatDateToISOString(new Date()),
    invoiceDate: formatDateToISOString(new Date()),
    dueDate: formatDateToISOString(new Date()),
    status: "Pending",
    amount: 2000000.5,
    description: "Charter Payment",
    reference: "REF-002",
    organizationId: commonOrgId,
    currencyId: mockAccounts[1].currencyId,
    currencyCode: mockCurrencies[1].code,
    budgetId: mockBudgets[0].id,
    budgetName: "Charter Budget",
    source: "Charter Client",
    accountId: mockAccounts[1].id,
    accountName: mockAccounts[1].name,
    vesselId: mockVessels[0].id,
    vesselName: mockVessels[0].name,
    exchangeRateId: mockExchangeRates[1].id,
    conversionRate: 1,
    type: "Income",
  },
  {
    id: "income-003",
    transactionDate: formatDateToISOString(
      subDays(new Date(new Date().setHours(6, 0, 0, 0)), 1)
    ),
    invoiceDate: formatDateToISOString(new Date()),
    dueDate: formatDateToISOString(new Date()),
    status: "Completed",
    amount: 1550000.5,
    description: "Crew Training Payment",
    reference: "REF-003",
    organizationId: commonOrgId,
    currencyId: mockAccounts[2].currencyId,
    currencyCode: mockCurrencies[2].code,
    budgetId: mockBudgets[1].id,
    budgetName: "Training Budget",
    source: "Training Institute",
    accountId: mockAccounts[2].id,
    accountName: mockAccounts[2].name,
    vesselId: mockVessels[1].id,
    vesselName: mockVessels[1].name,
    exchangeRateId: mockExchangeRates[2].id,
    conversionRate: 1,
    type: "Income",
  },
  {
    id: "income-004",
    transactionDate: formatDateToISOString(new Date()),
    invoiceDate: formatDateToISOString(new Date()),
    dueDate: formatDateToISOString(new Date()),
    status: "Pending",
    amount: 3000000.5,
    description: "Equipment Rental",
    reference: "REF-004",
    organizationId: commonOrgId,
    currencyId: mockAccounts[1].currencyId,
    currencyCode: mockCurrencies[1].code,
    budgetId: mockBudgets[0].id,
    budgetName: "Equipment Budget",
    source: "Rental Company",
    accountId: mockAccounts[1].id,
    accountName: mockAccounts[1].name,
    vesselId: mockVessels[0].id,
    vesselName: mockVessels[0].name,
    exchangeRateId: mockExchangeRates[1].id,
    conversionRate: 1,
    type: "Income",
  },
];

export const mockFundings: Funding[] = [
  {
    id: "funding-001",
    transactionDate: formatDateToISOString(
      new Date(
        new Date().setFullYear(
          new Date().getFullYear(),
          new Date().getMonth() - 1,
          15
        )
      )
    ),
    status: "Completed",
    amount: 4000000.5,
    description: "Initial Funding",
    reference: "REF-001",
    toAccountId: mockAccounts[0].id,
    toAccountName: mockAccounts[0].name,
    fundingSourceId: "fs-001",
    budgetId: mockBudgets[0].id,
    budgetName: "Initial Budget",
    type: "Funding",
  },
  {
    id: "funding-002",
    transactionDate: formatDateToISOString(new Date()),
    status: "Completed",
    amount: 8000000.5,
    description: "Additional Funding",
    reference: "REF-002",
    toAccountId: mockAccounts[2].id,
    toAccountName: mockAccounts[2].name,
    fundingSourceId: "fs-002",
    budgetId: mockBudgets[1].id,
    budgetName: "Additional Budget",
    type: "Funding",
  },
  {
    id: "funding-003",
    transactionDate: formatDateToISOString(new Date()),
    status: "Pending",
    amount: 12000000.5,
    description: "Emergency Funding",
    reference: "REF-003",
    toAccountId: mockAccounts[1].id,
    toAccountName: mockAccounts[1].name,
    fundingSourceId: "fs-003",
    budgetId: mockBudgets[0].id,
    budgetName: "Emergency Budget",
    type: "Funding",
  },
  {
    id: "funding-004",
    transactionDate: formatDateToISOString(new Date()),
    status: "Completed",
    amount: 16000000.5,
    description: "Final Funding",
    reference: "REF-004",
    toAccountId: mockAccounts[3].id,
    toAccountName: mockAccounts[3].name,
    fundingSourceId: "fs-004",
    budgetId: mockBudgets[1].id,
    budgetName: "Final Budget",
    type: "Funding",
  },
];

export const mockTransfers: Transfer[] = [
  {
    id: "transfer-001",
    transactionDate: formatDateToISOString(new Date()),
    status: "Completed",
    amount: 4000000.5,
    description: "Main Account Transfer",
    reference: "TRF-001",
    organizationId: commonOrgId,
    currencyId: mockAccounts[0].currencyId,
    currencyCode: mockCurrencies[0].code,
    fromAccountId: mockAccounts[0].id,
    fromAccountName: mockAccounts[0].name,
    toAccountId: mockAccounts[1].id,
    toAccountName: mockAccounts[1].name,
    budgetId: mockBudgets[0].id,
    budgetName: mockBudgets[0].name,
    vesselId: mockVessels[0].id,
    vesselName: mockVessels[0].name,
    exchangeRateId: mockExchangeRates[0].id,
    conversionRate: 1,
    type: "Transfer",
    ledgerEntries: [
      {
        accountId: mockAccounts[1].id,
        accountCurrencyId: mockAccounts[1].currencyId,
        accountCurrencyCode: mockCurrencies[1].code,
        conversionRate: 1,
        amount: 4000000.5,
      },
    ],
  },
  {
    id: "transfer-002",
    transactionDate: formatDateToISOString(new Date()),
    status: "Pending",
    amount: 8000000,
    description: "Secondary Account Transfer",
    reference: "TRF-002",
    organizationId: commonOrgId,
    currencyId: mockAccounts[2].currencyId,
    currencyCode: mockCurrencies[2].code,
    fromAccountId: mockAccounts[2].id,
    fromAccountName: mockAccounts[2].name,
    toAccountId: mockAccounts[3].id,
    toAccountName: mockAccounts[3].name,
    budgetId: mockBudgets[1].id,
    budgetName: mockBudgets[1].name,
    vesselId: mockVessels[1].id,
    vesselName: mockVessels[1].name,
    exchangeRateId: mockExchangeRates[1].id,
    conversionRate: 1,
    type: "Transfer",
    ledgerEntries: [],
  },
  {
    id: "transfer-003",
    transactionDate: formatDateToISOString(new Date()),
    status: "Completed",
    amount: 12000000,
    description: "Emergency Transfer",
    reference: "TRF-003",
    organizationId: commonOrgId,
    currencyId: mockAccounts[1].currencyId,
    currencyCode: mockCurrencies[1].code,
    fromAccountId: mockAccounts[1].id,
    fromAccountName: mockAccounts[1].name,
    toAccountId: mockAccounts[0].id,
    toAccountName: mockAccounts[0].name,
    budgetId: mockBudgets[0].id,
    budgetName: mockBudgets[0].name,
    vesselId: mockVessels[0].id,
    vesselName: mockVessels[0].name,
    exchangeRateId: mockExchangeRates[1].id,
    conversionRate: 1,
    type: "Transfer",
    ledgerEntries: [],
  },
  {
    id: "transfer-004",
    transactionDate: formatDateToISOString(new Date()),
    status: "Completed",
    amount: 19000000.5,
    description: "Final Transfer",
    reference: "TRF-004",
    organizationId: commonOrgId,
    currencyId: mockAccounts[3].currencyId,
    currencyCode: mockCurrencies[3].code,
    fromAccountId: mockAccounts[3].id,
    fromAccountName: mockAccounts[3].name,
    toAccountId: mockAccounts[2].id,
    toAccountName: mockAccounts[2].name,
    budgetId: mockBudgets[1].id,
    budgetName: mockBudgets[1].name,
    vesselId: mockVessels[1].id,
    vesselName: mockVessels[1].name,
    exchangeRateId: mockExchangeRates[1].id,
    conversionRate: 1,
    type: "Transfer",
    ledgerEntries: [],
  },
];

export const mockExpenseForeignAccount: Expense = {
  id: "expense-foreign-001",
  transactionDate: formatDateToISOString(new Date()),
  status: "Completed",
  amount: 1000,
  description: "Foreign currency expense",
  reference: "REF-FOREIGN-001",
  organizationId: commonOrgId,
  currencyId: mockCurrencies[1].id,
  currencyCode: mockCurrencies[1].code,
  budgetId: mockBudgets[0].id,
  vendorId: mockVendors[0].id,
  fromAccountId: mockAccounts[0].id,
  fromAccountName: mockAccounts[0].name,
  vesselId: mockVessels[0].id,
  attachments: [],
  type: "Expense",
  vendorName: mockVendors[0].name,
  version: 1,
  vesselFlagState: mockVessels[0].flagState,
  vesselName: mockVessels[0].name,
  exchangeRateId: mockExchangeRates[0].id,
  conversionRate: 1.08,
  ledgerEntries: [
    {
      accountId: mockAccounts[0].id,
      accountCurrencyId: mockAccounts[0].currencyId,
      accountCurrencyCode: mockCurrencies[0].code,
      conversionRate: 1.1,
      amount: 1100,
    },
  ],
};

export const mockTransferBothLegsForeign: Transfer = {
  id: "transfer-foreign-001",
  transactionDate: formatDateToISOString(new Date()),
  status: "Completed",
  amount: 1000,
  description: "Both legs foreign transfer",
  reference: "TRF-FOREIGN-001",
  organizationId: commonOrgId,
  currencyId: mockCurrencies[2].id,
  currencyCode: mockCurrencies[2].code,
  fromAccountId: mockAccounts[0].id,
  fromAccountName: mockAccounts[0].name,
  toAccountId: mockAccounts[1].id,
  toAccountName: mockAccounts[1].name,
  budgetId: mockBudgets[0].id,
  budgetName: mockBudgets[0].name,
  vesselId: mockVessels[0].id,
  vesselName: mockVessels[0].name,
  exchangeRateId: mockExchangeRates[0].id,
  conversionRate: 0.03,
  type: "Transfer",
  version: 1,
  ledgerEntries: [
    {
      accountId: mockAccounts[0].id,
      accountCurrencyId: mockAccounts[0].currencyId,
      accountCurrencyCode: mockCurrencies[0].code,
      conversionRate: 0.028,
      amount: 28,
    },
    {
      accountId: mockAccounts[1].id,
      accountCurrencyId: mockAccounts[1].currencyId,
      accountCurrencyCode: mockCurrencies[1].code,
      conversionRate: 0.032,
      amount: 32,
    },
  ],
};

export const mockTransactions: Transaction[] = [
  ...mockExpenses,
  ...mockIncomes,
  ...mockFundings,
  ...mockTransfers,
];

export const minimalTransaction: Pick<
  Expense,
  | "id"
  | "transactionDate"
  | "status"
  | "amount"
  | "currencyId"
  | "currencyCode"
  | "type"
> = {
  id: "minimal-transaction-id",
  type: "Expense",
  transactionDate: formatDateToISOString(new Date()),
  status: "Completed",
  amount: 1000000.5,
  currencyId: mockCurrencies[0].id,
  currencyCode: mockCurrencies[0].code,
};

export const getMockDocument = (status: string = "Processed"): DocumentType => {
  return {
    id: "doc-123",
    name: "Invoice #12345",
    type: "Invoice",
    status: status,
    reference: "INV-12345",
    vendorName: "Acme Supplies Ltd.",
    documentDate: formatDateToISOString(new Date()),
    uploadedAt: formatDateToISOString(new Date()),
    totalAmount: 1250.75,
    currencyCode: "EUR",
    itemCount: 3,
    processingError: "",
    lineItems: [
      {
        id: "item-1",
        description: "Engine maintenance",
        amount: 850.0,
        quantity: 1,
        unitPrice: 850.0,
        taxRate: 18,
        taxAmount: 153.0,
      },
      {
        id: "item-2",
        description: "Oil filters",
        amount: 225.75,
        quantity: 3,
        unitPrice: 75.25,
        taxRate: 18,
        taxAmount: 40.64,
      },
      {
        id: "item-3",
        description: "Labor hours",
        amount: 175.0,
        quantity: 2,
        unitPrice: 87.5,
        taxRate: 18,
        taxAmount: 31.5,
      },
    ],
  };
};

export const mockBudgetSummaries: BudgetSummary[] = [
  {
    id: mockBudgets[0].id,
    vesselName: mockVessels[0].name,
    vesselFlagCountry: mockVessels[0].flagState,
    vesselImageUrl: mockVessels[0].imageUrl,
    period: mockBudgets[0].name,
    startDate: mockBudgets[0].startDate,
    endDate: mockBudgets[0].endDate,
    status: mockBudgets[0].status,
    totalBudget: 100000,
    totalExpenses: 25000,
    totalIncome: 0,
    isArchived: false,
    defaultCurrencyCode: mockVessels[0].defaultCurrencyCode,
  },
  {
    id: mockBudgets[1].id,
    vesselName: mockVessels[1].name,
    vesselFlagCountry: mockVessels[1].flagState,
    vesselImageUrl: mockVessels[1].imageUrl,
    period: mockBudgets[1].name,
    startDate: mockBudgets[1].startDate,
    endDate: mockBudgets[1].endDate,
    status: mockBudgets[1].status,
    totalBudget: 85000,
    totalExpenses: 32000,
    totalIncome: 15000,
    isArchived: false,
    defaultCurrencyCode: mockVessels[1].defaultCurrencyCode,
  },
];

export const mockInvoiceLineItems: InvoiceLineItem[] = [
  {
    id: "invoicelineitem-1",
    description: "Engine parts description",
    amount: 1500,
    quantity: 1,
    unitPrice: 1500,
    vatRate: 10,
    vatAmount: 150,
    budgetSubcategoryId: mockBudgetSubCategories[0].id,
  },
  {
    id: "invoicelineitem-2",
    description: "Fuel description",
    amount: 500,
    quantity: 500,
    unitPrice: 1,
    vatRate: 10,
    vatAmount: 50,
    budgetSubcategoryId: mockBudgetSubCategories[1].id,
  },
];

export const mockInvoiceAttachments: InvoiceAttachment[] = [
  {
    id: "attachment-1",
    fileName: "invoice-1.pdf",
    filePath: "https://example.com/invoice-1.pdf",
    contentType: "application/pdf",
    fileSize: 1000,
    uploadedAt: "2023-09-01T11:00:00Z",
    uploadedBy: "user-maria-rudina-uuid",
  },
  {
    id: "attachment-2",
    fileName: "invoice-2.pdf",
    filePath: "https://example.com/invoice-2.pdf",
    contentType: "application/pdf",
    fileSize: 1000,
    uploadedAt: "2023-09-01T11:00:00Z",
    uploadedBy: "user-maria-rudina-uuid",
  },
];

export const mockInvoices: Invoice[] = [
  {
    id: "invoice-1",
    organizationId: commonOrgId,
    invoiceNumber: "111",
    issueDate: new Date().toISOString().split("T")[0],
    dueDate: new Date(addDays(new Date(), 10)).toISOString().split("T")[0],
    vendorId: mockVendors[0].id,
    vendorName: mockVendors[0].name,
    status: "PendingApproval",
    totalAmount: 1000,
    remainingBalance: 3000,
    currencyId: mockCurrencies[0].id,
    currencyCode: mockCurrencies[0].code,
    description: "Monthly supplies",
    reference: "PO-123",
    vesselId: mockVessels[0].id,
    vesselName: mockVessels[0].name,
    lineItems: mockInvoiceLineItems,
    attachments: mockInvoiceAttachments,
    payments: [
      {
        id: "payment-1",
        organizationId: commonOrgId,
        type: 1,
        currencyCode: mockCurrencies[0].code,
        transactionDate: "2023-10-15",
        invoiceDate: "2023-10-05",
        dueDate: "2023-11-05",
        status: "Pending",
        amount: 3500,
        description: "Payment for annual maintenance",
        reference: "TR-456",
        currencyId: mockCurrencies[0].id,
        budgetId: mockBudgets[0].id,
        vesselId: mockVessels[0].id,
        vendorId: mockVendors[0].id,
        accountId: mockAccounts[0].id,
        source: "Bank Transfer",
        fromAccountId: mockAccounts[1].id,
        toAccountId: mockVendors[0].id,
        exchangeRateId: "er-eur-usd-uuid",
        conversionRate: 1.0,
      },
      {
        id: "payment-2",
        organizationId: commonOrgId,
        type: 1,
        currencyCode: mockCurrencies[1].code,
        transactionDate: "2023-10-15",
        invoiceDate: "2023-10-05",
        dueDate: "2023-11-05",
        status: "Pending",
        amount: 3500,
        description: "Payment for annual maintenance",
        reference: "TR-456",
        currencyId: mockCurrencies[0].id,
        budgetId: mockBudgets[1].id,
        vesselId: mockVessels[1].id,
        vendorId: mockVendors[1].id,
        accountId: mockAccounts[1].id,
        source: "Bank Transfer",
        fromAccountId: mockAccounts[1].id,
        toAccountId: mockVendors[1].id,
        exchangeRateId: "er-eur-usd-uuid",
        conversionRate: 1.0,
      },
    ],
    hasPayments: true,
    version: 1,
    createdAt: "2023-10-01T10:00:00Z",
    createdBy: "user-maria-rudina-uuid",
  },
  {
    id: "invoice-1-2",
    organizationId: commonOrgId,
    invoiceNumber: "111222",
    issueDate: new Date(addDays(new Date(), 1)).toISOString().split("T")[0],
    dueDate: new Date(addDays(new Date(), 10)).toISOString().split("T")[0],
    vendorId: mockVendors[0].id,
    vendorName: mockVendors[0].name,
    status: "PendingApproval",
    totalAmount: 1200,
    remainingBalance: 3000,
    currencyId: mockCurrencies[0].id,
    currencyCode: mockCurrencies[0].code,
    description: "Monthly supplies",
    reference: "PO-123",
    vesselId: mockVessels[0].id,
    vesselName: mockVessels[0].name,
    lineItems: mockInvoiceLineItems,
    attachments: mockInvoiceAttachments,
    payments: [
      {
        id: "payment-1",
        organizationId: commonOrgId,
        type: 1,
        currencyCode: mockCurrencies[0].code,
        transactionDate: "2023-10-15",
        invoiceDate: "2023-10-05",
        dueDate: "2023-11-05",
        status: "Pending",
        amount: 3500,
        description: "Payment for annual maintenance",
        reference: "TR-456",
        currencyId: mockCurrencies[0].id,
        budgetId: mockBudgets[0].id,
        vesselId: mockVessels[0].id,
        vendorId: mockVendors[0].id,
        accountId: mockAccounts[0].id,
        source: "Bank Transfer",
        fromAccountId: mockAccounts[1].id,
        toAccountId: mockVendors[0].id,
        exchangeRateId: "er-eur-usd-uuid",
        conversionRate: 1.0,
      },
      {
        id: "payment-2",
        organizationId: commonOrgId,
        type: 1,
        currencyCode: mockCurrencies[1].code,
        transactionDate: "2023-10-15",
        invoiceDate: "2023-10-05",
        dueDate: "2023-11-05",
        status: "Pending",
        amount: 3500,
        description: "Payment for annual maintenance",
        reference: "TR-456",
        currencyId: mockCurrencies[0].id,
        budgetId: mockBudgets[1].id,
        vesselId: mockVessels[1].id,
        vendorId: mockVendors[1].id,
        accountId: mockAccounts[1].id,
        source: "Bank Transfer",
        fromAccountId: mockAccounts[1].id,
        toAccountId: mockVendors[1].id,
        exchangeRateId: "er-eur-usd-uuid",
        conversionRate: 1.0,
      },
    ],
    hasPayments: true,
    version: 1,
    createdAt: "2023-10-01T10:00:00Z",
    createdBy: "user-maria-rudina-uuid",
  },
  {
    id: "invoice-1-3",
    organizationId: commonOrgId,
    invoiceNumber: "111333",
    issueDate: new Date(addDays(new Date(), 2)).toISOString().split("T")[0],
    dueDate: new Date(addDays(new Date(), 30)).toISOString().split("T")[0],
    vendorId: mockVendors[0].id,
    vendorName: mockVendors[0].name,
    status: "PendingApproval",
    totalAmount: 1200,
    remainingBalance: 3000,
    currencyId: mockCurrencies[0].id,
    currencyCode: mockCurrencies[0].code,
    description: "Monthly supplies",
    reference: "PO-123",
    vesselId: mockVessels[0].id,
    vesselName: mockVessels[0].name,
    lineItems: mockInvoiceLineItems,
    attachments: mockInvoiceAttachments,
    payments: [],
    hasPayments: false,
    version: 1,
    createdAt: "2023-10-01T10:00:00Z",
    createdBy: "user-maria-rudina-uuid",
  },
  {
    id: "invoice-1-4",
    organizationId: commonOrgId,
    invoiceNumber: "111444",
    issueDate: new Date(addDays(new Date(), 3)).toISOString().split("T")[0],
    dueDate: new Date(addDays(new Date(), 40)).toISOString().split("T")[0],
    vendorId: mockVendors[0].id,
    vendorName: mockVendors[0].name,
    status: "PendingApproval",
    totalAmount: 1400,
    remainingBalance: 4000,
    currencyId: mockCurrencies[1].id,
    currencyCode: mockCurrencies[1].code,
    description: "Monthly supplies",
    reference: "PO-1234",
    vesselId: mockVessels[0].id,
    vesselName: mockVessels[0].name,
    lineItems: mockInvoiceLineItems,
    attachments: mockInvoiceAttachments,
    payments: [],
    hasPayments: false,
    version: 1,
    createdAt: "2023-10-01T10:00:00Z",
    createdBy: "user-maria-rudina-uuid",
  },
  {
    id: "invoice-1-5",
    organizationId: commonOrgId,
    invoiceNumber: "111555",
    issueDate: new Date(subMonths(new Date(), 1)).toISOString().split("T")[0],
    dueDate: new Date(subMonths(new Date(), 1)).toISOString().split("T")[0],
    vendorId: mockVendors[0].id,
    vendorName: mockVendors[0].name,
    status: "PendingApproval",
    totalAmount: 1400,
    remainingBalance: 4000,
    currencyId: mockCurrencies[1].id,
    currencyCode: mockCurrencies[1].code,
    description: "Monthly supplies",
    reference: "PO-12345",
    vesselId: mockVessels[0].id,
    vesselName: mockVessels[0].name,
    lineItems: mockInvoiceLineItems,
    attachments: mockInvoiceAttachments,
    payments: [],
    hasPayments: false,
    version: 1,
    createdAt: "2023-10-01T10:00:00Z",
    createdBy: "user-maria-rudina-uuid",
  },
  {
    id: "invoice-2",
    organizationId: commonOrgId,
    invoiceNumber: "222",
    issueDate: new Date().toISOString().split("T")[0],
    dueDate: new Date(addDays(new Date(), 10)).toISOString().split("T")[0],
    vendorId: mockVendors[1].id,
    vendorName: mockVendors[1].name,
    status: "PartiallyPaid",
    totalAmount: 2000,
    remainingBalance: 0,
    currencyId: mockCurrencies[1].id,
    currencyCode: mockCurrencies[1].code,
    description: "Annual maintenance",
    reference: "PO-124",
    vesselId: mockVessels[1].id,
    vesselName: mockVessels[1].name,
    lineItems: [],
    attachments: mockInvoiceAttachments,
    payments: [],
    hasPayments: true,
    version: 1,
    createdAt: "2023-10-05T14:00:00Z",
    createdBy: "user-john-smith-uuid",
  },
  {
    id: "invoice-overdue-1",
    organizationId: commonOrgId,
    invoiceNumber: "333",
    issueDate: new Date(addDays(new Date(), 2)).toISOString().split("T")[0],
    dueDate: new Date(addDays(new Date(), 20)).toISOString().split("T")[0],
    vendorId: mockVendors[0].id,
    vendorName: mockVendors[0].name,
    status: "Overdue",
    totalAmount: 2100,
    remainingBalance: 6200,
    currencyId: mockCurrencies[0].id,
    currencyCode: mockCurrencies[0].code,
    description: "Overdue supplies",
    reference: "PO-101",
    vesselId: mockVessels[1].id,
    vesselName: mockVessels[1].name,
    lineItems: [],
    attachments: mockInvoiceAttachments,
    payments: [],
    hasPayments: false,
    version: 1,
    createdAt: "2023-09-01T11:00:00Z",
    createdBy: "user-maria-rudina-uuid",
  },
];

export const mockInvoiceStatistics: InvoiceStatistics[] = [
  {
    vesselId: mockVessels[0].id,
    vesselName: mockVessels[0].name,
    defaultCurrencyCode: mockCurrencies[0].code,
    draftCount: 1,
    pendingApprovalCount: 2,
    approvedCount: 5,
    rejectedCount: 0,
    partiallyPaidCount: 1,
    fullyPaidCount: 10,
    cancelledCount: 0,
    overdueCount: 1,
    totalAmount: 50000,
    totalPaidAmount: 45000,
    totalOutstandingAmount: 5000,
    totalOverdueAmount: 1200,
  },
  {
    vesselId: mockVessels[1].id,
    vesselName: mockVessels[1].name,
    defaultCurrencyCode: mockCurrencies[1].code,
    draftCount: 0,
    pendingApprovalCount: 1,
    approvedCount: 8,
    rejectedCount: 1,
    partiallyPaidCount: 2,
    fullyPaidCount: 15,
    cancelledCount: 1,
    overdueCount: 0,
    totalAmount: 75000,
    totalPaidAmount: 70000,
    totalOutstandingAmount: 5000,
    totalOverdueAmount: 0,
  },
];

export const mockInvoiceSummaries: InvoiceSummary[] = [
  {
    vesselId: mockVessels[0].id,
    vesselName: mockVessels[0].name,
    vesselImageUrl: mockVessels[0].imageUrl || "",
    vesselFlagState: mockVessels[0].flagState,
    totalInvoices: 50000,
    totalInvoiceCount: 20,
    pendingInvoices: 5000,
    pendingInvoiceCount: 3,
    overdueInvoices: 1200,
    overdueInvoiceCount: 1,
    lastInvoiceDate: "2023-10-01",
    defaultCurrencyCode: mockCurrencies[0].code,
  },
  {
    vesselId: mockVessels[1].id,
    vesselName: mockVessels[1].name,
    vesselImageUrl: mockVessels[1].imageUrl || "",
    vesselFlagState: mockVessels[1].flagState,
    totalInvoices: 75000,
    totalInvoiceCount: 28,
    pendingInvoices: 5000,
    pendingInvoiceCount: 1,
    overdueInvoices: 0,
    overdueInvoiceCount: 0,
    lastInvoiceDate: "2023-10-05",
    defaultCurrencyCode: mockCurrencies[1].code,
  },
];

export const mockInvoicePayments: InvoicePayment[] = [
  {
    id: "payment-1",
    organizationId: commonOrgId,
    type: 1,
    currencyCode: mockCurrencies[1].code,
    transactionDate: "2023-10-15",
    invoiceDate: "2023-10-05",
    dueDate: "2023-11-05",
    status: "Pending",
    amount: 3500,
    description: "Payment for annual maintenance",
    reference: "TR-456",
    currencyId: mockCurrencies[1].id,
    budgetId: mockBudgets[0].id,
    vesselId: mockVessels[1].id,
    vendorId: mockVendors[1].id,
    accountId: mockAccounts[1].id,
    source: "Bank Transfer",
    fromAccountId: mockAccounts[1].id,
    toAccountId: mockVendors[1].id,
    exchangeRateId: "er-eur-usd-uuid",
    conversionRate: 1.0,
  },
];

export const mockMaintenanceTasks: MaintenanceTask[] = [
  {
    id: "task-001",
    organizationId: commonOrgId,
    vesselId: mockVessels[0].id,
    name: "Engine Oil Change",
    path: "Maintenance/Engine/Oil Change",
    description: "Regular engine oil change",
    parentNodeId: "parent-001",
    parentNodeName: "Engine Maintenance",
    version: 1,
    taskDetail: {
      id: "task-detail-001",
      nodeId: "task-001",
      unitToExecute: "hours",
      notes: "Use synthetic oil",
      scheduleType: "RecurringRunningHours",
      runningHoursInterval: 200,
      lastCompletedDate: new Date(subDays(new Date(), 30)),
      lastCompletedRunningHours: 800,
      nextRunningHours: 1000,
      trackedComponentNodeId: "engine-001",
      trackedComponentRunningHours: 950,
      version: 1,
      assignees: [
        {
          id: "30623859-78c7-4bf1-b33f-a4d6e5391e61",
          fullName: "Mevlüt Can Tuna",
        },
      ],
    },
  },
  {
    id: "task-002",
    organizationId: commonOrgId,
    vesselId: mockVessels[0].id,
    name: "Hull Inspection",
    path: "Maintenance/Hull/Inspection",
    description: "Annual hull inspection",
    version: 1,
    taskDetail: {
      id: "task-detail-002",
      nodeId: "task-002",
      notes: "Check for corrosion",
      scheduleType: "RecurringCalendar",
      calendarIntervalDays: 365,
      lastCompletedDate: new Date(subDays(new Date(), 180)),
      nextDate: new Date(addDays(new Date(), 185)),
      version: 1,
      assignees: [
        {
          id: "30623859-78c7-4bf1-b33f-a4d6e5391e61",
          fullName: "Mevlüt Can Tuna",
        },
      ],
    },
  },
  {
    id: "task-003",
    organizationId: commonOrgId,
    vesselId: mockVessels[0].id,
    name: "Safety Equipment Check",
    path: "Maintenance/Safety/Equipment Check",
    description: "Check all safety equipment",
    version: 1,
    taskDetail: {
      id: "task-detail-003",
      nodeId: "task-003",
      scheduleType: "NonRecurringDate",
      dueDate: new Date(addDays(new Date(), 30)),
      version: 1,
      assignees: [
        {
          id: "30623859-78c7-4bf1-b33f-a4d6e5391e61",
          fullName: "Mevlüt Can Tuna",
        },
      ],
    },
  },
  {
    id: "task-004",
    organizationId: commonOrgId,
    vesselId: mockVessels[1].id,
    name: "Generator Service",
    path: "Maintenance/Generator/Service",
    description: "Generator maintenance",
    version: 1,
    taskDetail: {
      id: "task-detail-004",
      nodeId: "task-004",
      scheduleType: "RecurringCombined",
      calendarIntervalDays: 180,
      runningHoursInterval: 500,
      version: 1,
      assignees: [
        {
          id: "30623859-78c7-4bf1-b33f-a4d6e5391e61",
          fullName: "Mevlüt Can Tuna",
        },
      ],
    },
  },
];

export const mockMaintenanceExecutionEntries: MaintenanceExecutionEntry[] = [
  {
    id: "execution-001",
    taskNodeId: "task-001",
    taskNodeName: "Engine Oil Change",
    executionDate: new Date(subDays(new Date(), 30)),
    runningHoursAtExecution: 800,
    notes: "Completed successfully",

    version: 1,
    attachments: [],
  },
  {
    id: "execution-002",
    taskNodeId: "task-001",
    taskNodeName: "Engine Oil Change",
    executionDate: new Date(subDays(new Date(), 230)),
    runningHoursAtExecution: 600,
    notes: "Previous service",

    version: 1,
    attachments: [],
  },
  {
    id: "execution-003",
    taskNodeId: "task-002",
    taskNodeName: "Hull Inspection",
    executionDate: new Date(subDays(new Date(), 180)),
    notes: "No issues found",

    version: 1,
    attachments: [],
  },
];
